import React, { useEffect, useState } from 'react'
import { Box, Grid, CircularProgress, Typography } from '@mui/material'
import { TypoGraph, Icon } from 'components'
import { reportException } from 'tools/logs'
import { MilesByMonth } from 'interfaces/ISummaryReport'
import strings from 'strings'
import styles from './estimatedMilesTotal.module.scss'
import { getMilesByMonth } from 'api'
import moment from 'moment'
import { formatDateMMDDYYYY } from 'tools/helper'

const r = strings.NB_SUMMARY_REPORT
const t = strings.NB_SUMMARY_REPORT.ESTIMATED_MILES

interface EstimatedMilesByMonth {
  month: string
  estimatedMiles: number | null
  activeUnits: number | null
  avgPerUnit: number | null
}

interface EstimatedMilesData {
  summary: {
    totalMiles: number
    period: string
  }
  breakdownByMonth: EstimatedMilesByMonth[]
}

interface EstimatedMilesProps {
  onLoadingChange?: (isLoading: boolean) => void
  useKilometers?: boolean
}

const EstimatedMiles: React.FC<EstimatedMilesProps> = ({ onLoadingChange, useKilometers = false }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<EstimatedMilesData | null>(null)

  const getDistanceLabel = (key: string): string => {
    if (!useKilometers) return t[key as keyof typeof t] as string

    const label = t[key as keyof typeof t] as string
    return label?.replace(/Miles?/gi, (match) =>
      match.charAt(0) === match.charAt(0).toUpperCase() ? 'Kilometers' : 'kilometers'
    ) || label
  }

  const carrierId = localStorage.getItem('carrierId') || localStorage.getItem('companyId') || ''
  const onboardingDate = localStorage.getItem('onboardingDate')
  const endDate = new Date()
  const startDate = new Date()
  startDate.setMonth(startDate.getMonth() - 12)

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0]
  }

  const formatMonthYear = (dateString: string): string => {
    const [year, month] = dateString.split('-')
    const date = new Date(parseInt(year), parseInt(month) - 1)
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  const formatPeriod = (startDate: string, endDate: string): string => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    return `${start.getMonth() + 2}/${start.getFullYear()} - ${end.getMonth() + 1
      }/${end.getFullYear()}`
  }

  const transformApiData = (
    apiData: MilesByMonth,
    onboardingDateString: string | null
  ): EstimatedMilesData => {
    const onboardingMoment = onboardingDateString
      ? moment(onboardingDateString)
      : null

    const breakdownByMonth = apiData.breakdownByMonth.map((monthData) => {
      const monthMoment = moment(monthData.date)

      const isBeforeOnboarding =
        onboardingMoment && monthMoment.isBefore(onboardingMoment, 'month')

      return {
        month: formatMonthYear(monthData.date),
        estimatedMiles: isBeforeOnboarding && monthData.totalGpsMiles === 0
          ? null
          : Math.round(monthData.totalGpsMiles),
        activeUnits: isBeforeOnboarding && monthData.totalVehicles === 0 ? null : monthData.totalVehicles,
        avgPerUnit: isBeforeOnboarding && monthData.avgGpsMilesVehicles === 0
          ? null
          : Math.round(monthData.avgGpsMilesVehicles),
      }
    })

    return {
      summary: {
        totalMiles: Math.round(apiData.summary.totalGpsMiles),
        period: formatPeriod(
          apiData.summary.period.startDate,
          apiData.summary.period.endDate
        ),
      },
      breakdownByMonth,
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!carrierId) return

      setIsLoading(true)
      try {
        const apiData = await getMilesByMonth(
          carrierId,
          formatDate(startDate),
          formatDate(endDate),
          useKilometers
        )
        const transformedData = transformApiData(apiData, onboardingDate)
        setData(transformedData)
      } catch (err) {
        reportException(err)
        setData(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [carrierId])

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const calculatedOnText = `${r.CALCULATED_ON +
    formatDateMMDDYYYY(moment().subtract(1, 'days').toISOString())
    } 
      ${r.DAYS_OF_DATA_PRE} ${endDate && startDate ? moment(endDate).diff(moment(startDate), 'days') : 0
    } 
          ${r.DAYS_OF_DATA_POST}`

  const TotalMileage = () => (
    <Grid mb={2} data-testid="estimated-miles-total-block">
      <div className={styles.panelHeader} data-testid="estimated-miles-total-header">
        <div className={styles.panelIcon}>
          <Icon
            materialIcon="TrendingUp"
            boxClassName={styles.trendingIcon}
            color="rgb(108, 124, 147)"
          />
        </div>
        <Typography variant="h3" sx={{ fontWeight: "600" }}>Total Kilometers</Typography>
      </div>
      <Box className={styles.totalMilesPaper} data-testid="estimated-miles-total-paper">
        {isLoading ? (
          <CircularProgress size={40} className={styles.circularProgress} data-testid="estimated-miles-total-spinner" />
        ) : (
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'rgb(5, 80, 179)', mb: 0.5 }} data-testid="estimated-miles-value-total">
              {data && data.summary.totalMiles
                ? Math.round(data.summary.totalMiles).toLocaleString()
                : t.NO_ESTIMATED_MILEAGE_DATA}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {getDistanceLabel('TOTAL_ESTIMATED_MILES_12_MONTHS')}
            </Typography>
            <Typography variant="body2" color="text.secondary" data-testid="estimated-miles-value-period">
              {data?.summary.period ?? '--'}
            </Typography>
          </Box>
        )}
      </Box>
    </Grid>
  )

  const MonthlyBreakdown = () => (
    <Grid data-testid="estimated-miles-monthly-breakdown-block">
      <div className={styles.panelHeader} data-testid="estimated-miles-monthly-header">
        <div className={styles.panelIcon}>
          <Icon
            materialIcon="TrendingUp"
            boxClassName={styles.trendingIcon}
            color="rgb(108, 124, 147)"
          />
        </div>
        <Typography variant="h3" sx={{ fontWeight: "600" }}>
          {getDistanceLabel('TOTAL_ESTIMATED_MILES_12_MONTHS')}
        </Typography>
      </div>

      <Grid container spacing={2} data-testid="estimated-miles-monthly-grid">
        {isLoading ? (
          <Grid item xs={12} className={styles.loadingContainer} data-testid="estimated-miles-monthly-loading">
            <CircularProgress size={30} className={styles.loadingSpinner} data-testid="estimated-miles-monthly-spinner" />
          </Grid>
        ) : (
          data?.breakdownByMonth.map((month, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={idx} data-testid={`estimated-miles-month-card-${idx}`}>
              <Box className={styles.monthlyBreakdownPaper} data-testid={`estimated-miles-month-paper-${idx}`}>
                <Typography variant="h6" className={styles.monthTitle} data-testid={`estimated-miles-month-name-${idx}`}>
                  {month.month}
                </Typography>

                <Typography variant="h4" className={styles.estimatedMiles} data-testid={`estimated-miles-month-value-${idx}`}>
                  {month.estimatedMiles !== null
                    ? month.estimatedMiles.toLocaleString()
                    : 'N/A'}
                </Typography>
                <Typography variant="caption" className={styles.label}>
                  {getDistanceLabel('ESTIMATED_MILES')}
                </Typography>

                <Typography variant="body2" className={styles.dataValue} data-testid={`estimated-miles-month-active-units-${idx}`}>
                  {month.activeUnits !== null ? month.activeUnits : 'N/A'}
                </Typography>
                <Typography variant="caption" className={styles.label}>
                  {t.ACTIVE_UNITS}
                </Typography>

                <Typography variant="body2" className={styles.dataValue} data-testid={`estimated-miles-month-avg-per-unit-${idx}`}>
                  {month.avgPerUnit !== null
                    ? month.avgPerUnit.toLocaleString()
                    : 'N/A'}
                </Typography>
                <Typography variant="caption" className={styles.label}>
                  {getDistanceLabel('AVG_PER_UNIT')}
                </Typography>
              </Box>
            </Grid>
          ))
        )}
      </Grid>
    </Grid>
  )

  return (
    <Grid mb={1} data-testid="estimated-miles-section">
      <TypoGraph variant="h2" mb={1} content={getDistanceLabel('ESTIMATED_MILES_TITLE')} dataTestId="estimated-miles-title" />
      <TypoGraph
        variant="body2"
        color="text.secondary"
        sx={{ display: 'block', mb: 3 }}
        dataTestId="estimated-miles-calculated-on"
      >
        {calculatedOnText}
      </TypoGraph>
      <TotalMileage />
      <MonthlyBreakdown />
    </Grid>
  )
}

export default EstimatedMiles
